import { useMemo, useState } from 'react';

// Helper.
import Helper from '../../../helpers/Helper';
import ConditionalRender from '../../../helpers/ConditionalRender';

// Types.
import HostTarget from '../../../types/host/HostTarget';
import ChartPlotLine from '../../../types/ChartPlotLine';

// Constants.
import { CHART_DATA_TYPE, DATA_FAILED, DATA_LOADED, DATA_LOADING, VIEW_MODE } from '../../Constants';

import TimeSeriesChart from '../../chart/echarts/TimeSeriesChart';
import { useDiskVolumeStatistic } from './useDiskVolumeStatistic';

function DiskUtilisationChart(props: { host: HostTarget, plotLines?: ChartPlotLine[] }) {
    const [mode, setMode] = useState<number>(VIEW_MODE.BASIC);
    const { loading, basicSeries, volumeSeries } = useDiskVolumeStatistic(props.host);

    const timePeak = useMemo(() => {
        if (!basicSeries.length) {
            return 0;
        }
        const values = basicSeries.flatMap(set => set.data.map(point => point[1]));
        return Helper.getPeakValue(values) ?? 0;
    }, [basicSeries]);

    const hasData = basicSeries.length > 0;

    return (
        <div className="card collapsible">
            <div className="card-header">
                <i className="fal fa-chart-bar fa-fw" aria-hidden="true"/>
                Average disk utilisation over time
                <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                   data-bs-target="#collapseAvailableDiskCharts" aria-expanded="false"
                   aria-controls="collapseAvailableDiskCharts"/>
                <ConditionalRender if={hasData}>
                    <div className="btn-group float-end">
                        <button type="button" className="btn btn-xsm btn-primary float-end ms-1"
                                onClick={() => setMode(mode === VIEW_MODE.BASIC ? VIEW_MODE.DETAIL : VIEW_MODE.BASIC)}>
                            {((mode === VIEW_MODE.BASIC) ? 'Show Breakdown' : 'Show Summary')}
                        </button>
                    </div>
                </ConditionalRender>
            </div>
            <div id="collapseAvailableDiskCharts" className="card-body collapse show p-0">
                <div className="row row-cols-1">
                    <div className="col p-3">
                        <ConditionalRender if={mode === VIEW_MODE.BASIC}>
                            <h4 className='icons-chart-title'>
                                Average disk utilisation
                                <span className="peak">Peak Average utilisation: {timePeak.toFixed(2)}%</span>
                            </h4>
                        </ConditionalRender>

                        <ConditionalRender if={loading === DATA_LOADING}>
                            <div className="w-100 text-center text-muted mt-3">
                                <div className="loader spinner chartSpinner">
                                </div>
                                <p className="mt-3">
                                    Loading data...
                                </p>
                            </div>
                        </ConditionalRender>

                        <ConditionalRender if={loading === DATA_LOADED && mode === VIEW_MODE.BASIC && hasData}>
                            <TimeSeriesChart series={basicSeries} tooltipType={CHART_DATA_TYPE.PERCENTAGE}
                                             plotLines={props.plotLines} legend={true} height={300}/>
                        </ConditionalRender>

                        <ConditionalRender if={loading === DATA_LOADED && mode === VIEW_MODE.DETAIL}>
                            {volumeSeries.map(volume => (
                                <div key={volume.disk}>
                                    <h4 className='icons-chart-title'>
                                        Average disk utilisation for {volume.disk}
                                    </h4>
                                    <TimeSeriesChart series={volume.series} tooltipType={CHART_DATA_TYPE.GENERIC}
                                                     plotLines={props.plotLines} legend={true}/>
                                </div>
                            ))}
                        </ConditionalRender>

                        <ConditionalRender if={(loading === DATA_LOADED && !hasData) || loading === DATA_FAILED}>
                            <div className="w-100 text-center text-muted my-3">
                                <i className="fal fa-ban fa-fw fa-2x"></i>
                                <p className="mt-3">
                                    No data found
                                </p>
                            </div>
                        </ConditionalRender>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DiskUtilisationChart;
